import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';

import './index.css';
import TransactionItemCandlesChart from './Candles';
import CandleView from './CandleView';
import {
  CbrtRangeState,
  IndicatorConfig,
  MrcState,
  RSIItem,
  WakeupTimes,
} from '../../../../../ProfitCalculations/types';
import { BotTimeFrames } from '../../../../../Detail/types';
import { colors } from '../../../../../../colors';
import {
  formatBalance,
  formatPercentage,
  formatPrice,
} from '../../../../../../helpers/NumberFormatter';
import TransactionItemStructure from '../../../../../../components/TransactionItem';
import { GenericTransactionInfo } from '../../../../../../types/Transaction';
import BalanceIndciator from '../../../../../../components/BalanceIndicator';

interface EnhancedProps {
  index: number;
  transaction: GenericTransactionInfo;
  compactTransactions: GenericTransactionInfo[];
  compactTransactionIndex: number | null;
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: GenericTransactionInfo[];
  showCandles: boolean;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  indicatorConfig?: IndicatorConfig;
  rsiIndicatorValuesOppositeOrder?: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder?: RSIItem[] | undefined;
}

interface Props {
  index: number;
  transaction: GenericTransactionInfo;
  compactTransactionIndex: number | null;
  compactTransactions: GenericTransactionInfo[];
  currentProfit: number | undefined;
  currentPriceChange: number | undefined;
  compactTransactionsLength: number;
  clusteredTransactions: GenericTransactionInfo[];
  toggleCompactTransaction?: () => void;
  isCompact?: boolean;
  isVeryFirst?: boolean;
  isFirstClusteredTransaction?: boolean;
  clusteredTransactionIndex?: number;
  totalClusteredTransactions?: number;
  showCandles: boolean;
  isChartView: boolean;
  setIsChartView: React.Dispatch<React.SetStateAction<boolean>>;
  cbrtRangeStates: CbrtRangeState[] | undefined;
  mrcStates: MrcState[] | undefined;
  timeFrames: BotTimeFrames | undefined;
  showCurrentProfit: boolean;
  wakeupTimes?: WakeupTimes;
  indicatorConfig?: IndicatorConfig;
  rsiIndicatorValuesOppositeOrder?: RSIItem[] | undefined;
  rsiIndicatorValuesSameOrder?: RSIItem[] | undefined;
}

const TransactionItem: React.FC<Props> = (props: Props) => {
  const {
    transaction,
    compactTransactions,
    currentProfit,
    currentPriceChange,
    isCompact = true,
    isVeryFirst = false,
    showCandles,
    isChartView,
    setIsChartView,
    cbrtRangeStates,
    mrcStates,
    timeFrames,
    showCurrentProfit,
    wakeupTimes,
    clusteredTransactions,
    indicatorConfig,
    rsiIndicatorValuesOppositeOrder,
    rsiIndicatorValuesSameOrder,
  } = props;

  const transactionOrderColor = useCallback(
    (orderType: 'BUY' | 'SELL' | undefined) =>
      typeof orderType === undefined
        ? colors.blue
        : orderType === 'BUY'
        ? colors.green
        : colors.red,
    [],
  );

  const getProfitValue = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return profit;
    }

    if (profit === null) {
      return formatPercentage(0);
    }

    if (typeof profit === 'string') {
      return formatPercentage(parseFloat(profit));
    }

    return formatPercentage(profit);
  }, []);

  const getProfitColor = useCallback((profit: number | string | null) => {
    if (profit === 'PENDING') {
      return colors.orange;
    }

    if (profit === null) {
      return colors.white;
    }

    if (typeof profit === 'string') {
      return parseFloat(profit) >= 0 ? colors.green : colors.red;
    }

    return profit >= 0 ? colors.green : colors.red;
  }, []);

  const transactionLabel = useMemo(() => {
    if (transaction.transactionType.includes('STOP_LOSS')) {
      return 'SL';
    }

    if (transaction.transactionType.includes('TAKE_PROFIT')) {
      return 'TP';
    }

    return typeof transaction.transactionIndex !== 'undefined'
      ? transaction.transactionIndex
      : 'x';
  }, [transaction.transactionIndex, transaction.transactionType]);

  const handleCandlesPress = useCallback(() => {
    setIsChartView(true);
  }, [setIsChartView]);

  const handleCloseChartView = useCallback(() => {
    setIsChartView(false);
  }, [setIsChartView]);

  const transactionPrice = useMemo(() => {
    const price = transaction.orderPrice || 0;

    return formatPrice(price);
  }, [transaction]);

  const shouldShowCurrentProfit =
    showCurrentProfit && (isVeryFirst || isCompact);

  return (
    <TransactionItemStructure
      circleLabel={transactionLabel}
      circleColor={transactionOrderColor(transaction.orderType)}
      date={moment(transaction.transactionTimestamp).format('D MMM YYYY HH:mm')}
      currentProfit={shouldShowCurrentProfit ? currentProfit : undefined}
      currentPriceChange={
        shouldShowCurrentProfit ? currentPriceChange : undefined
      }
      transactionType={transaction.transactionType}
      content={
        isChartView ? (
          <div
            style={{
              display: 'flex',
              padding: 8,
              height: '70vh',
              width: '100%',
            }}
          >
            <CandleView
              isCompact={isCompact}
              transaction={transaction}
              compactTransactions={compactTransactions}
              clusteredTransactions={clusteredTransactions}
              handleCloseChartView={handleCloseChartView}
              cbrtRangeStates={cbrtRangeStates}
              mrcStates={mrcStates}
              timeFrames={timeFrames}
              wakeupTimes={wakeupTimes}
              indicatorConfig={indicatorConfig}
              rsiIndicatorValuesOppositeOrder={rsiIndicatorValuesOppositeOrder}
              rsiIndicatorValuesSameOrder={rsiIndicatorValuesSameOrder}
            />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateRows: 'repeat(3, auto)',
              gridTemplateColumns: 'auto auto 1fr',
              color: colors.white,
              padding: 8,
              paddingLeft: 32,
              paddingRight: 32,
              columnGap: 50,
              rowGap: 10,
            }}
          >
            <div
              style={{
                gridRow: '1 / 2',
                gridColumn: '1 / 3',
              }}
            >
              <span
                style={{
                  color: colors.gray,
                  fontSize: 22,
                  marginBottom: 0,
                  textWrap: 'nowrap',
                }}
              >
                {isCompact ? 'Average Price:' : 'Price:'}
              </span>
              <span
                style={{ color: colors.white, fontSize: 26, marginLeft: 4 }}
              >
                {transactionPrice}
              </span>
            </div>
            <div
              style={{
                gridRow: '2 / 3',
                gridColumn: '1 / 2',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Stable quantity:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.stableQuantity, true, 0)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Asset quantity:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.assetQuantity, false, 6)}
                </span>
              </div>
              {!!transaction.takeProfitSize && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      color: colors.gray,
                      fontSize: 14,
                      textWrap: 'nowrap',
                    }}
                  >
                    Size percentage:
                  </span>
                  <span
                    style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                  >
                    {`${Math.floor(transaction.takeProfitSize * 100)}`}%
                  </span>
                </div>
              )}
              {!!transaction.stopLossThreshold && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      color: colors.gray,
                      fontSize: 14,
                      textWrap: 'nowrap',
                    }}
                  >
                    Stop Loss threshold:
                  </span>
                  <span
                    style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                  >
                    -{`${Math.floor(transaction.stopLossThreshold * 100)}`}%
                  </span>
                </div>
              )}
            </div>
            <div
              style={{
                gridRow: '2 / 3',
                gridColumn: '2 / 3',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Stable profit:
                </span>
                <span
                  style={{
                    color:
                      transaction.orderType === 'SELL'
                        ? getProfitColor(transaction.profitInUsdt)
                        : colors.white,
                    fontSize: 18,
                    marginTop: -5,
                  }}
                >
                  {transaction.orderType === 'SELL'
                    ? getProfitValue(transaction.profitInUsdt)
                    : '0%'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Asset profit:
                </span>
                <span
                  style={{
                    color:
                      transaction.orderType === 'BUY'
                        ? getProfitColor(transaction.profitInAsset)
                        : colors.white,
                    fontSize: 18,
                    marginTop: -5,
                  }}
                >
                  {transaction.orderType === 'BUY'
                    ? getProfitValue(transaction.profitInAsset)
                    : '0%'}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '3 / 4',
                gridColumn: '1 / 2',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Stable balance:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.currentStableBalance, true, 0)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Asset balance:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.currentAssetBalance, false, 4)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Balance
                </span>
                <BalanceIndciator
                  stableBalancePercentage={
                    100 *
                    (transaction.currentStableBalance /
                      (transaction.currentAssetBalance *
                        transaction.orderPrice +
                        transaction.currentStableBalance))
                  }
                  assetBalancePercentage={
                    100 *
                    ((transaction.currentAssetBalance *
                      transaction.orderPrice) /
                      (transaction.currentAssetBalance *
                        transaction.orderPrice +
                        transaction.currentStableBalance))
                  }
                />
              </div>
            </div>
            <div
              style={{
                gridRow: '3 / 4',
                gridColumn: '2 / 3',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Fee value
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.currentFee)}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span
                  style={{
                    color: colors.gray,
                    fontSize: 14,
                    textWrap: 'nowrap',
                  }}
                >
                  Total fees paid:
                </span>
                <span
                  style={{ color: colors.white, fontSize: 18, marginTop: -5 }}
                >
                  {formatBalance(transaction.totalFee)}
                </span>
              </div>
            </div>
            <div
              style={{
                gridRow: '1 / 4',
                gridColumn: '3 / 4',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {showCandles && (
                <div
                  onClick={handleCandlesPress}
                  style={{
                    height: '100%',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  {/** Candles */}
                  <TransactionItemCandlesChart
                    transaction={transaction}
                    compactTransactions={compactTransactions}
                  />
                </div>
              )}
            </div>
          </div>
        )
      }
    />
  );
};

const EnhancedTransactionItem: React.FC<EnhancedProps> = (
  props: EnhancedProps,
): any => {
  const { clusteredTransactions, compactTransactionIndex } = props;
  const [showCompactTransaction, setShowCompactTransaction] = useState(true);

  const [isChartView, setIsChartView] = useState(false);

  const toggleCompactTransaction = useCallback(() => {
    setShowCompactTransaction(!showCompactTransaction);
  }, [showCompactTransaction]);

  if (showCompactTransaction) {
    return (
      <TransactionItem
        {...props}
        toggleCompactTransaction={toggleCompactTransaction}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
      />
    );
  }

  if (clusteredTransactions.length) {
    return clusteredTransactions.map((t, index) => {
      return (
        <TransactionItem
          key={t.transactionIndex}
          {...props}
          transaction={t}
          isCompact={false}
          isVeryFirst={compactTransactionIndex === 0 && index === 0}
          toggleCompactTransaction={toggleCompactTransaction}
          isFirstClusteredTransaction={index === 0}
          clusteredTransactionIndex={index}
          totalClusteredTransactions={clusteredTransactions.length}
          isChartView={isChartView}
          setIsChartView={setIsChartView}
        />
      );
    });
  }

  return (
    <TransactionItem
      {...props}
      toggleCompactTransaction={toggleCompactTransaction}
      isChartView={isChartView}
      setIsChartView={setIsChartView}
    />
  );
};

export default React.memo(EnhancedTransactionItem);
